/**
 * Glue layer between game state, Rive layer, Pixi chip animations, hub, and
 * the DOM UI. Mirrors the imperative side-effect orchestration in
 *   packages/amgbandarqq_v2/lib/presentation/bloc/game_bloc.dart
 * + the Stack composition in game_screen.dart.
 *
 * INPUT flow (driven by Rive events emitted from the betting UI artboard):
 *   placeBetClick {betID}     → tryPlaceBet(betID)
 *   evtClickChips {chipIndex} → selectChip(i)
 *   evtClearClick             → clearAll
 *   evtButtonPlayClick        → confirm
 *
 * OUTPUT flow (driven by phase transitions + Rive/hub events):
 *   betting phase             → fireStartBet, girl(1), setBetLimits
 *   confirm()                 → POST /action/bet → applyRoundResult → reveal
 *   result reveal             → setDominoes, setScores, setVS(true),
 *                                setYellowBoxes(winningIds)
 *   showResult                → DOM win/lose panel
 *   newRound                  → resetBandarResult, hardClear chips, startBet
 */
import type { BetItemId, GameRound } from '../types/game';
import {
  BET_ITEM_IDS,
  BET_ID_TO_BOX_INDEX,
  isBetItemId,
} from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_PASSWORD,
  GAME_KEY_MULTI,
  GAME_KEY_SINGLE,
  NEW_ROUND_DELAY_MS,
  NO_MORE_BET_HOLD_MS,
  RESULT_CHIPS_DELAY_MS,
  RESULT_NEW_ROUND_DELAY_MS,
  RESULT_WIN_LOSE_DELAY_MS,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { ChipLayer } from '../pixi/chipLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
import { showModeSelectionDialog, type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import type { ResultPanel } from '../ui/resultPanel';
import { parseResultString } from './resultParser';
import { debugLog, error, warn } from '../util/logger';

/**
 * Multiplayer only: end the visible bet countdown this many seconds before
 * the server's real betting-window close. The batched bet submission fires
 * when the countdown reaches 0, so this buffer gives it time to reach the
 * backend before betting closes and the round starts playing.
 */
const BET_SUBMIT_BUFFER_SEC = 2;

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  chips: ChipLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  resultPanel: ResultPanel;
  params: UrlParams;
  onInteractive?: () => void;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid = '';

  // Result-sequence guard flags — cleared each round.
  private didStartResultSequence = false;
  private didShowWinLose = false;
  private didResetAfterResult = false;
  // Balance settlement is deferred to the WIN/LOSE reveal so the displayed
  // (available) balance stays "bet deducted" through the domino reveal and
  // only reflects winnings once the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Multiplayer: the result reveal reached the settle point but the server's
  // Result event (which carries the post-round balance) hasn't arrived yet.
  // We hold the bets in place until it does (see applySettlement) so the
  // displayed available balance never flashes the pre-settlement wallet.
  private awaitingSettleBalance = false;
  // Bumped each round start; settlement captures it before the async balance
  // fetch and bails if a new round started meanwhile.
  private roundToken = 0;
  // Guards applyResultSettlement to once per round.
  private didApplyResultSettlement = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;
  private sequenceTimers: number[] = [];

  // Multiplayer.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      this.d.state.setPhase('modeSelection');
      this.d.hud.setStatus('Select mode');
      const mode: GameMode = await showModeSelectionDialog();
      debugLog('[Orchestrator] mode selected:', mode);

      // Tell Rive which mode is active (controls component visibility).
      this.d.rive.setSingleMode(mode === 'single');

      if (mode === 'multi') {
        await this.startMultiplayer();
      } else {
        this.startBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] Using token+gsid from URL params (no login)');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });
      debugLog('[Orchestrator] openGame OK, currentGameUrl=', session.currentGameUrl);

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);
      this.applyServerConfig(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
        });
      }
    }
  }

  private applyServerConfig(
    chips: number[],
    minBet: number,
    maxBet: number,
  ): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    this.d.state.setConfig({
      minBet: nextMin,
      maxBet: nextMax,
      chipValues: nextChips,
    });
  }

  // =================================================================
  // Rive event bindings
  // =================================================================

  private bindRiveEvents(): void {
    this.d.rive.on('placeBetClick', (payload) => {
      const raw = (payload.betID ?? payload.betId ?? payload.id) as
        | number
        | undefined;
      if (typeof raw !== 'number') return;
      if (!isBetItemId(raw)) {
        debugLog('[Orchestrator] placeBetClick unknown betID', raw);
        return;
      }
      this.tryPlaceBet(raw);
    });

    this.d.rive.on('evtClickChips', (payload) => {
      const i = (payload.chipIndex ?? payload.index ?? payload.chipsAmt) as
        | number
        | undefined;
      if (typeof i === 'number') this.d.state.selectChip(i);
    });

    this.d.rive.on('evtClearClick', () => this.clearAll());

    this.d.rive.on('evtButtonPlayClick', () => void this.confirm());

    // NOTE: BandarQQ's result flow is FIXED-TIMER driven, mirroring the Flutter
    // v2 bloc (`_onStartResultSequence` resultStop timeline). We deliberately do
    // NOT drive the sequence off `evtResultStop` — that event fires whenever the
    // .riv VS animation loop ends (uncontrolled timing) and would tear the
    // result down early. Single-player runs the timeline from confirm(); the
    // multiplayer Result hub event runs it from handleResult(). (`evtYouWinEnd`/
    // `evtYouLoseEnd` aren't emitted by bandarqq.riv at all.)
  }

  // =================================================================
  // Player actions
  // =================================================================

  tryPlaceBet(betItemId: BetItemId): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    if (s.isMultiplayer && !s.multiplayerCanBet) return;

    const chipValue = s.selectedChipValue;
    const chipIndex = s.selectedChipIndex;
    const ok = s.placeBet(betItemId, chipValue);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', betItemId, chipValue);
      return;
    }
    // Visual chip flies in immediately and the available balance drops at
    // once (req: balance reflects the bet immediately). Bets are NOT sent to
    // the backend here — single-player submits on Play (confirm) and
    // multiplayer submits when the bet countdown ends — so they stay locally
    // clearable until then (req: player can still clear the bets).
    this.d.chips.placeChip(betItemId, chipValue, chipIndex);

    // Push per-area amount text into the betting artboard.
    this.d.rive.setBetBoxAmount(BET_ID_TO_BOX_INDEX[betItemId], s.bets.amountFor(betItemId));
  }

  clearAll(): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, once the countdown ends we auto-submit and lock the
    // bets — disallow clearing past that point.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;
    s.clearBets();
    this.d.chips.hardClear();
    this.d.rive.clearAllBetBoxAmounts();
  }

  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;
    if (this.d.state.isMultiplayer) {
      debugLog('[Orchestrator] confirm() ignored in multiplayer mode');
      return;
    }

    const bets = this.d.state.bets.toBetLines();
    if (bets.length === 0) return;

    this.d.state.setPhase('bettingClosed');
    // "No more bet" banner (triEndBet). The reveal is gated behind this so the
    // banner plays before the dominoes appear — mirrors game_bloc.dart
    // _onConfirmBets → noMoreBet() + _scheduleNoMoreBetEndFallback.
    this.d.rive.fireEndBet();
    this.d.hud.setStatus('No more bets');
    const noMoreBetAt = performance.now();
    this.d.state.setPhase('waitingResult');

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      const parsed = parseResultString(result.result);
      this.gid = result.nextRoundId || this.gid;

      const totalBet = this.d.state.bets.total;

      const round: GameRound = {
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        playerScore: parsed.playerScore,
        bankerScore: parsed.bankerScore,
        winner: parsed.winner,
        winningBetIds: parsed.winningBetIds,
        dominoSpriteIndices: parsed.dominoSpriteIndices,
        needsShuffle: parsed.needsShuffle,
        totalWinLose: result.totalWinLose,
        totalBet,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
      };
      this.d.state.applyRoundResult(round);
      // Defer the balance update to the WIN/LOSE reveal so the displayed
      // balance stays "bet deducted" through the domino reveal and only
      // reflects winnings when the result is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      this.d.state.setPhase('dealing');
      this.d.hud.setStatus('Dealing…');

      // Reveal only after the "no more bet" banner has had its hold (whichever
      // is later: the banner window or the API round-trip), then run the timed
      // result sequence. Mirrors game_bloc.dart: the reveal
      // (_startResultAfterNoMoreBet) fires once both the API result and the
      // ~900 ms noMoreBet animation are done.
      const elapsed = performance.now() - noMoreBetAt;
      const wait = Math.max(0, NO_MORE_BET_HOLD_MS - elapsed);
      this.scheduleTimer(() => {
        this.revealResult(round);
        this.startResultSequence();
      }, wait);
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.state.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * One PlaceBet is sent per INDIVIDUAL chip placed (not the per-box total):
   * the hub validates each amount against the configured chip denominations
   * and rejects a summed total with "Invalid Chip Amount", and the server
   * adds each amount to the player's stake, so per-chip lines sum to the same
   * stake as the original per-chip increments. Idempotent: the
   * didSubmitMpBets guard prevents a double-submit when both the countdown
   * tick and EndBet fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Phase / animation orchestration
  // =================================================================

  /** Start a fresh betting round (single-player path). */
  private startBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.didStartResultSequence = false;
    this.didShowWinLose = false;
    this.didResetAfterResult = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;

    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.rive.resetBandarResult();
    this.d.rive.clearAllBetBoxAmounts();
    this.d.state.setPhase('betting');

    const cfg = this.d.state.config;
    this.d.rive.setBetLimits(`$ ${cfg.minBet}`, `$ ${cfg.maxBet}`);
    this.d.rive.setBalanceText(formatCurrency(this.d.state.availableBalance));
    this.d.rive.setBetAmountText('0');
    this.d.rive.setWinAmountText('0');

    this.d.rive.fireStartBet();
    this.d.rive.triggerGirlAnim(1);

    this.d.hud.setStatus('Place your bets');
  }

  /**
   * Apply the round's dominoes + scores into the topItem artboard and
   * highlight the winning bet boxes on the MainBetResult state machine.
   * Mirrors game_bloc.dart `_onStartResultSequence` / `_handleResult`.
   */
  private revealResult(round: GameRound): void {
    // Order + effects mirror game_bloc.dart _startResultAfterNoMoreBet.
    this.d.rive.setDominoSpriteIndices(round.dominoSpriteIndices);
    this.d.rive.setResultScores(round.playerScore, round.bankerScore);
    this.d.rive.setResetDominoRandom(0);
    this.d.rive.setWinningYellowBoxes(round.winningBetIds);
    this.d.rive.showBandarResult(true);
    // Girl "take chip" animation as the result is shown.
    this.d.rive.triggerGirlAnim(2);
  }

  /**
   * Post-reveal sequence:
   *   - t=0  : chip animation by win/lose
   *   - t=2s : show win/lose panel (if there was a bet)
   *   - t=6s : safety-net new-round teardown
   */
  private startResultSequence(): void {
    if (this.didStartResultSequence) return;
    // Bail BEFORE latching the guard: if the round data isn't ready yet,
    // latching here would permanently wedge the sequence — the win/lose timer
    // (and its applySettlement) would never be scheduled, so the round's
    // win/loss would silently never apply. Leaving the guard unset lets a later
    // trigger (evtResultStop, the confirm() safety net, or the next Result)
    // start it once currentRound exists.
    const round = this.d.state.currentRound;
    if (!round) return;
    this.didStartResultSequence = true;
    this.cancelTimers();

    this.d.state.setPhase('showResult');

    // resultStop timeline (mirrors game_bloc.dart _onStartResultSequence):
    //   t=2s  chips fly to winner/dealer
    //   t=5s  win/lose panel + balance settle
    //   t=13s teardown → next betting round

    // t=2s — chips fly by win/lose. Read the win/lose live so a late
    // multiplayer Result settlement is reflected.
    this.scheduleTimer(() => {
      const wl = this.d.state.currentRound?.totalWinLose ?? round.totalWinLose;
      if (wl > 0) {
        this.d.chips.animateWinningChips(new Set<BetItemId>(round.winningBetIds));
      } else if (wl < 0) {
        this.d.chips.animateLosingChips();
      } else {
        this.d.chips.animateTie();
      }
    }, RESULT_CHIPS_DELAY_MS);

    // t=5s — win/lose panel + deferred balance settle.
    this.scheduleTimer(() => {
      if (this.didShowWinLose) return;
      this.didShowWinLose = true;
      // Reflect the post-round balance now that the result is shown (req 2).
      // Clears the resolved bets in the same commit so the available balance
      // lands exactly on the server's settled value.
      this.applySettlement();
      const wl = this.d.state.currentRound?.totalWinLose ?? round.totalWinLose;
      if (wl === 0) {
        // No bet / push — straight back to betting.
        this.resetToBettingIfNeeded();
        return;
      }
      this.d.resultPanel.show(wl > 0, Math.abs(wl));
      this.d.rive.setWinAmountText(formatCurrency(wl > 0 ? wl : -wl));
      this.d.hud.setStatus(
        wl > 0
          ? `YOU WIN ${formatCurrency(wl)}`
          : `YOU LOSE ${formatCurrency(-wl)}`,
      );
      this.d.state.setPhase('result');
    }, RESULT_WIN_LOSE_DELAY_MS);

    // t=13s — teardown → next betting round.
    this.scheduleTimer(() => this.resetToBettingIfNeeded(), RESULT_NEW_ROUND_DELAY_MS);
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the win/lose timer and again from the teardown safety nets.
   * If the server balance isn't known yet (a late multiplayer Result), the
   * bets stay in place; handleResult finalizes the moment it arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance != null) {
      // Server's post-round balance is known — finalize atomically.
      this.didSettle = true;
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      // Multiplayer: the reveal reached the settle point but the server's
      // Result event (carrying the post-round balance) hasn't arrived yet.
      // Do NOT clear the bets here — the displayed balance is
      // `wallet − pending bets`, so clearing the bets while the wallet still
      // holds the pre-settlement value would make the balance jump UP to the
      // full (pre-bet) wallet for a moment (the "balance flashes to the old
      // value" bug). Instead, leave the bets so the display stays
      // bet-deducted, and let handleResult finalize atomically the instant the
      // balance arrives.
      this.awaitingSettleBalance = true;
    }
  }

  /**
   * Tear-down between rounds. Mirrors `_resetToBettingIfNeeded` in
   * the Dragon Tiger orchestrator. For BandarQQ:
   *   - resetBandarResult() (clear domino + yellow boxes + VS overlay)
   *   - hardClear chips
   *   - in single-player → startBettingRound (which fires startBet popup)
   *   - in multiplayer  → wait for StartGame / StartBet from the hub
   */
  private resetToBettingIfNeeded(): void {
    if (this.didResetAfterResult) return;
    const p = this.d.state.phase;
    if (p !== 'result' && p !== 'showResult' && p !== 'dealing') return;
    this.didResetAfterResult = true;

    // Safety net: ensure the balance was settled even if the win/lose timer
    // was skipped (e.g. a Rive evtYouWinEnd fired early).
    this.applySettlement();

    this.cancelTimers();
    this.d.resultPanel.hide();

    this.scheduleTimer(() => {
      this.d.rive.resetBandarResult();
      this.d.rive.clearAllBetBoxAmounts();
      this.d.chips.hardClear();
      if (!this.d.state.isMultiplayer) {
        this.startBettingRound();
      }
    }, Math.min(NEW_ROUND_DELAY_MS, 1000));
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      this.d.rive.setBetAmountText(formatCurrency(next.bets.total));
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
      // Re-push every box's running total in case the diff happened via
      // a bulk mutation (e.g. resetForNewRound).
      this.d.rive.setUserBetBoxAmounts(next.bets);
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.phase !== prev.phase ||
      next.multiplayerCanBet !== prev.multiplayerCanBet
    ) {
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  /** Push the available balance (wallet − pending bets) to HUD + Rive. */
  private renderBalance(): void {
    const avail = this.d.state.availableBalance;
    this.d.hud.setBalance(avail);
    this.d.rive.setBalanceText(formatCurrency(avail));
  }

  /**
   * Whether chip / play / clear controls should be live. Single-player: the
   * betting phase. Multiplayer: additionally gated by `multiplayerCanBet`
   * (the hub's StartBet/EndBet and our countdown-end submit).
   */
  private canInteract(s: GameStateSnapshot): boolean {
    return s.phase === 'betting' && (!s.isMultiplayer || s.multiplayerCanBet);
  }

  // =================================================================
  // Multiplayer — hub bootstrap + event router
  // =================================================================

  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting hub…');

    try {
      await this.d.hub.connect();

      // JoinGame's sessionId is the URL-DECODED bearer token, NOT the gsid.
      // Matches game_screen.dart:153-167 in DT v2 (same pattern in bandarqq).
      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      debugLog('[Orchestrator] joining hub, gameKey=', GAME_KEY_MULTI);
      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.state.setMultiplayerActive(false);
    }
  }

  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const status =
            typeof payload[0] === 'string' ? (payload[0] as string) : null;
          if (status) {
            return { _status: status, ...(item as Record<string, unknown>) };
          }
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  private handleHubEvent(event: HubEvent): void {
    const env = this.normalizeEnvelope(event.payload);

    // Index-based dedup — env.Index is monotonic within a round.
    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }

    // Index counter resets to 1 each new round. State === 'restart' marks
    // the boundary — clear the dedup tracker or round 2's StartGame/StartBet
    // will be silently dropped.
    if (env && String(env.State ?? '').toLowerCase() === 'restart') {
      this.lastMpEventIndex = null;
    }

    switch (event.name) {
      case '_connection':
        debugLog('[Hub] connection state', event.payload);
        break;

      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;

      case 'PlayerLeft':
        this.handlePlayerLeft(env);
        break;

      case 'StartGame':
        this.handleStartGame(env);
        break;

      case 'StartBet':
        this.handleStartBet(env);
        break;

      case 'EndBet':
        this.handleEndBet(env);
        break;

      case 'StartPlay':
        this.handleStartPlay(env);
        break;

      case 'Result':
        this.handleResult(env);
        break;

      case 'PlaceBet':
        this.handlePlaceBetAck(env);
        break;

      case 'GetMessages':
        // Server replays a backlog — each event fires its own listener.
        break;

      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
  }

  // -----------------------------------------------------------------
  // Hub handlers
  // -----------------------------------------------------------------

  private handlePlayerJoined(env: Envelope): void {
    if (env?._status === 'Fail') {
      const msg = String(env.Content ?? 'Failed to join multiplayer table');
      error('[Hub] PlayerJoined Fail:', msg);
      this.d.state.setError(msg);
      this.d.hud.setStatus('ERROR: ' + msg);
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
      return;
    }

    const game = (env?.Data && typeof env.Data === 'object'
      ? (env.Data as { Game?: unknown }).Game
      : undefined) as Record<string, unknown> | undefined;
    if (game) {
      const chips = parseChipsField(game.Chips ?? game.chips);
      const min = asNumber(game.MinBet ?? game.minBet);
      const max = asNumber(game.MaxBet ?? game.maxBet);
      if (chips.length > 0 || min > 0 || max > 0) {
        this.applyServerConfig(chips, min, max);
      }
    }

    // Also extract our balance — server includes it in PlayerJoined.
    this.updateBalanceFromEnvelope(env);
  }

  private handlePlayerLeft(env: Envelope): void {
    const msg = String(env?.Content ?? 'You have been disconnected.');
    debugLog('[Hub] PlayerLeft kick:', msg);
    this.d.state.setPlayerKickedMessage(msg);
    this.stopMultiplayer();
    this.d.state.setPhase('modeSelection');
    this.d.hud.setStatus('Session ended — please reload');
  }

  private handleStartGame(env: Envelope): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    this.cancelTimers();

    this.roundToken++;
    this.didStartResultSequence = false;
    this.didShowWinLose = false;
    this.didResetAfterResult = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;

    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.rive.resetBandarResult();
    this.d.rive.clearAllBetBoxAmounts();
    this.d.resultPanel.hide();
    this.d.state.setPhase('newRound');
    this.updateBalanceFromEnvelope(env);
  }

  private handleStartBet(env: Envelope): void {
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    debugLog('[Hub] StartBet — countdown', seconds, 's');
    // Fresh bet window — allow a new batched submission this round.
    this.didSubmitMpBets = false;
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);
    this.d.rive.fireStartBet();
    this.d.rive.triggerGirlAnim(1);
    this.d.hud.setStatus('Place your bets');
    // End the visible countdown BET_SUBMIT_BUFFER_SEC seconds early so the
    // batched submit (at countdown 0) reaches the backend before the real
    // window closes and play starts.
    this.startBetCountdown(Math.max(0, seconds - BET_SUBMIT_BUFFER_SEC));
    this.updateBalanceFromEnvelope(env);
  }

  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    this.d.rive.fireEndBet();
    this.d.hud.setStatus('No more bets');
  }

  private handleStartPlay(env: Envelope): void {
    const raw = String(env?.Result ?? '');
    if (!raw) return;
    let parsed;
    try {
      parsed = parseResultString(raw);
    } catch (e) {
      warn('[Hub] StartPlay parse failed:', e, raw);
      return;
    }
    this.d.state.setMultiplayerResultString(raw);
    const totalBet = this.d.state.bets.total;
    const round: GameRound = {
      roundId: '',
      nextRoundId: '',
      playerScore: parsed.playerScore,
      bankerScore: parsed.bankerScore,
      winner: parsed.winner,
      winningBetIds: parsed.winningBetIds,
      dominoSpriteIndices: parsed.dominoSpriteIndices,
      needsShuffle: parsed.needsShuffle,
      // WinLose comes via the Result event; placeholder for now.
      totalWinLose: 0,
      totalBet,
      positiveWinAmount: 0,
      hasPositiveWin: false,
    };
    this.d.state.applyRoundResult(round);
    this.d.state.setPhase('dealing');

    // Reveal cards immediately; the Result hub event will fill in win/lose.
    this.revealResult(round);
  }

  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();

    if (state === 'restart' || env?.StartTime || env?.EndTime) {
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) {
        debugLog('[Hub] Result/restart — restart countdown', seconds, 's');
        this.d.state.setMultiplayerGameState('restart');
        this.startRestartCountdown(seconds);
        return;
      }
    }

    // Start the visual result sequence right away — it's purely cosmetic
    // (domino reveal, win/lose panel) and must NOT wait on the balance
    // round-trip below. Idempotent: Rive's evtResultStop (or the forced
    // fallback) may have already started it.
    if (!this.didStartResultSequence) this.startResultSequence();

    // Settle off the AUTHORITATIVE post-round balance. The backend stopped
    // returning a usable per-bet figure (WinLost is 0 even on a loss) and the
    // Result event no longer carries a per-user Balance, so the wallet can't be
    // derived locally — we read it from the server (/action/balance). Async.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event AND returns WinLost=0 even on a loss, so neither the wallet
   *      nor the net P&L can be derived locally; the only correct source is the
   *      server's own balance. NOTE: /action/getinfo does NOT carry a balance
   *      (config only), so we must use /action/balance, the same endpoint the
   *      startup balance fetch uses.
   *
   * Async because of the balance round-trip, so it captures the round token up
   * front and bails if a new round started before the balance arrived — a slow
   * fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);

    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog(
        '[Hub] Result settlement dropped — round advanced during balance fetch',
      );
      return;
    }

    this.applyResultSettlement(winLose, balance);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * Returns null on failure so the caller can fall back to the locally-tracked
   * wallet rather than zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: defer the balance to the WIN/LOSE
   * reveal (settling atomically if the reveal is already waiting), patch the
   * round's win/lose, and start the cosmetic sequence if not already running.
   * `balance` is the authoritative post-round wallet (from the Result event or
   * /action/balance); null only when both sources failed, in which case we fall
   * back to `balance + winLose` (the legacy local computation).
   */
  private applyResultSettlement(winLose: number, balance: number | null): void {
    // Once per round — a retransmitted settlement Result must not re-run this.
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    const balanceAfter = balance ?? this.d.state.balance + winLose;

    this.d.state.setMultiplayerWinLose(winLose);
    // Defer the balance update to the WIN/LOSE reveal, like single-player
    // (req 2).
    this.pendingSettleBalance = balanceAfter;
    // If the reveal already reached the settle point and was only waiting on
    // this balance, finalize now (atomic balance update + bet clear). This is
    // the multiplayer path where the Result event lands after the local result
    // sequence — settling atomically here avoids clearing bets early (which
    // would flash the pre-settlement wallet).
    if (this.awaitingSettleBalance && !this.didSettle) {
      this.didSettle = true;
      this.awaitingSettleBalance = false;
      this.d.state.settle(balanceAfter);
    }

    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({
        ...cur,
        totalWinLose: winLose,
        positiveWinAmount: winLose > 0 ? winLose : 0,
        hasPositiveWin: winLose > 0,
      });
    }

    if (!this.didStartResultSequence) this.startResultSequence();
  }

  /**
   * PlaceBet ack — server confirmed (part of) our batched bet submission.
   *
   * No-op for balance: bets are submitted as a single batch when the
   * countdown ends and the displayed balance is optimistic (wallet −
   * pending bets). The authoritative wallet is re-synced from the Result
   * event at settlement, so applying the ack's per-bet balance here would
   * double-count the deduction. Local bets are NOT cleared here either —
   * matches the 2026-01-22 baccarat maxBet fix — they're cleared atomically
   * at settlement (and on the next StartGame).
   */
  private handlePlaceBetAck(_env: Envelope): void {
    // intentionally empty (see above)
  }

  private updateBalanceFromEnvelope(env: Envelope): void {
    if (!env) return;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const balance = extractBalanceForUser(env.Data, userName);
    if (balance != null) this.d.state.setBalance(balance);
  }

  // -----------------------------------------------------------------
  // Multiplayer countdown timers (1-Hz tick)
  // -----------------------------------------------------------------

  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
  }

  // =================================================================
  // Helpers
  // =================================================================

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// Keep the export silencer.
export const _bandarqqBetIds = BET_ITEM_IDS;

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

// =====================================================================
// Multiplayer envelope helpers
// =====================================================================

type Envelope = Record<string, unknown> | null;

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

/**
 * Sum the current user's WinLose values from a Result envelope's Data array.
 * Field-name priority: WinLost → WinLose → winLost → winLose. Entries with
 * empty/missing username are info-only broadcasts and must be skipped.
 */
function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;

  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      sum += asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
    }
    return sum;
  }

  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    return asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
  }

  return 0;
}

/**
 * Extract the current user's Balance from a Result / PlaceBet envelope's
 * Data field. Three rules:
 *   1. Field name probe order: Username → username → UserName → userName.
 *   2. Entries with empty/missing username are info-only broadcasts —
 *      SKIP them, do not match.
 *   3. Matched entry with `Balance: null` returns null so the caller
 *      preserves the prior balance.
 */
function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;

  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const b = item.Balance ?? item.balance;
      if (b == null) return null;
      const n = typeof b === 'number' ? b : Number(b);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (!u || u !== userNameLower) return null;
    const b = m.Balance ?? m.balance;
    if (b == null) return null;
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

function pickUserName(item: Record<string, unknown>): string {
  const raw =
    item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}
